/**
 * LiquidationBanner — Golden CTA shown in Dashboard when the user has
 * at least one liquidation in a non-terminal stage. Deep-links into
 * <LiquidationJourney />.
 */
import React, { useEffect, useState } from 'react';
import { motion } from 'framer-motion';
import { Trophy, ArrowRight, Sparkles } from 'lucide-react';
import api from '@/store/api';

const TERMINAL_STAGES = new Set(['liquidated', 'cancelled_by_user', 'cancelled_by_operator']);

export default function LiquidationBanner({ onOpen }) {
  const [liq, setLiq] = useState(null);

  useEffect(() => {
    let cancelled = false;
    api('/api/liquidations/mine')
      .then((res) => {
        if (cancelled) return;
        const active = (res.liquidations || []).find(l => !TERMINAL_STAGES.has(l.stage));
        setLiq(active || null);
      })
      .catch(() => { /* silent */ });
    return () => { cancelled = true; };
  }, []);

  if (!liq) return null;

  const isKompaPlus = liq.modality === 'kompa_plus';
  const headline = isKompaPlus
    ? '🎉 Tu beneficio KOMPA+ está listo'
    : '🚀 Tu Plan Acelerado alcanzó el 65%';
  const detail = isKompaPlus
    ? `Cashback + ahorro de €${(liq.liquidableEur + liq.cashbackEur).toFixed(2)} disponibles`
    : `Selecciona tu proveedor financiero para el 35% restante`;

  return (
    <motion.div
      initial={{ opacity: 0, y: -6 }}
      animate={{ opacity: 1, y: 0 }}
      transition={{ duration: 0.3 }}
      className="mx-6 md:mx-8 mb-4"
      data-testid="liquidation-banner"
    >
      <button
        type="button"
        onClick={() => onOpen?.(liq.id)}
        className="w-full group relative overflow-hidden rounded-2xl
                   bg-gradient-to-br from-kompa-navy via-kompa-navy-hover to-kompa-navy
                   p-5 text-left shadow-lg transition-transform hover:scale-[1.01]"
        data-testid="liquidation-banner-cta"
      >
        {/* Decorative sparkle */}
        <Sparkles className="absolute -right-4 -top-4 h-24 w-24 rotate-12 text-kompa-orange/20"
                  strokeWidth={1.2} />
        <div className="relative flex items-start gap-4">
          <div className="flex-shrink-0 rounded-full bg-kompa-orange p-3 shadow-md">
            <Trophy className="h-6 w-6 text-kompa-navy" strokeWidth={2.5} />
          </div>
          <div className="flex-1 min-w-0">
            <p className="text-[10px] uppercase tracking-[2px] font-bold text-kompa-orange mb-1">
              Beneficio disponible
            </p>
            <p className="text-base font-extrabold text-white leading-tight">{headline}</p>
            <p className="mt-1 text-xs text-white/75 leading-snug">{detail}</p>
            <div className="mt-3 inline-flex items-center gap-1.5 text-xs font-bold text-kompa-orange">
              Reclamar mi beneficio
              <ArrowRight className="h-3.5 w-3.5 transition-transform group-hover:translate-x-0.5" />
            </div>
          </div>
        </div>
      </button>
    </motion.div>
  );
}
